import { useEffect, useMemo, useState } from 'react';
import { formatWholeNumber } from '../game/util/numberFormat';

const paddedDigits = (value, width, shift = 0) => {
  const digits = String(Math.abs(value)).split('');
  const availableWidth = Math.max(0, width - shift);
  return [
    ...Array.from({ length: Math.max(0, availableWidth - digits.length) }, () => ''),
    ...digits,
    ...Array.from({ length: shift }, () => ''),
  ];
};

const columnDigit = (digit, width, index) => {
  if (!digit) return '';
  const beginsGroup = index > 0 && (width - index) % 3 === 0;
  return beginsGroup ? ',' + digit : digit;
};

function EqualGroupsVisual({ tutorial, step }) {
  const isZeroGroups = tutorial.a === 0;

  return (
    <div className="tmag-multiplication-visual">
      {isZeroGroups ? (
        <div className="tmag-multiplication-empty-groups">
          <span aria-hidden="true">✦</span>
          <strong>0 grupos</strong>
        </div>
      ) : (
        <div
          className="tmag-multiplication-groups"
          style={{ '--tmag-group-count': Math.max(1, tutorial.groups.length) }}
        >
          {tutorial.groups.map(group => (
            <div className="tmag-multiplication-group" key={group.number}>
              <span className="tmag-multiplication-group-label">Grupo {group.number}</span>
              <div
                className="tmag-multiplication-crystals"
                style={{ '--tmag-crystal-columns': Math.min(3, Math.max(1, tutorial.b)) }}
              >
                {group.crystals.map(crystal => (
                  <span className="tmag-multiplication-crystal" key={crystal} aria-label="Gema verde" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {step.showRepeatedAddition ? (
        isZeroGroups ? (
          <p className="tmag-multiplication-empty-sum">No hay grupos que sumar.</p>
        ) : (
          <div className="tmag-multiplication-repeated">
            {tutorial.repeatedAddends} = <strong>{formatWholeNumber(tutorial.result)}</strong>
          </div>
        )
      ) : null}

      {step.showProduct ? (
        <div className="tmag-multiplication-product">
          {formatWholeNumber(tutorial.a)} × {formatWholeNumber(tutorial.b)} = <strong>{formatWholeNumber(tutorial.result)}</strong>
        </div>
      ) : null}
    </div>
  );
}

function PlanetTwoMultiplicationColumnsVisual({ tutorial, step }) {
  const width = tutorial.gridWidth;
  const carries = new Map((step.carries || []).map(carry => [carry.gridColumn, carry.value]));
  const visibleResult = new Set(step.resultVisible || []);
  const topDigits = paddedDigits(tutorial.a, width);
  const multiplierDigits = paddedDigits(tutorial.b, width);
  const resultDigits = paddedDigits(tutorial.result, width);

  return (
    <div
      className="tmag-worked-columns tmag-worked-multiplication-columns"
      style={{ '--tmag-columns': width }}
      aria-label={`${formatWholeNumber(tutorial.a)} multiplicado por ${formatWholeNumber(tutorial.b)}`}
    >
      <div className="tmag-worked-row tmag-worked-carries">
        {Array.from({ length: width }, (_, index) => (
          <span key={index}>{carries.get(index) || ''}</span>
        ))}
      </div>
      <div className="tmag-worked-row tmag-worked-top">
        {topDigits.map((digit, index) => (
          <span className={step.activeTopColumn === index ? 'is-active' : ''} key={index}>
            {columnDigit(digit, width, index)}
          </span>
        ))}
      </div>
      <div className="tmag-worked-row tmag-worked-addend tmag-worked-multiplier">
        {multiplierDigits.map((digit, index) => (
          <span className={step.activeMultiplierColumn === index ? 'is-active' : ''} key={index}>
            {columnDigit(digit, width, index)}
          </span>
        ))}
      </div>
      <div className="tmag-worked-line" aria-hidden="true" />
      <div className="tmag-worked-row tmag-worked-result">
        {resultDigits.map((digit, index) => (
          <span className={visibleResult.has(index) ? 'is-visible' : ''} key={index}>
            {visibleResult.has(index) ? columnDigit(digit, width, index) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function MultiplicationColumnsVisual({ tutorial, step }) {
  const width = tutorial.gridWidth;
  const carries = new Map((step.carries || []).map(carry => [carry.gridColumn, carry.value]));
  const visibleResult = new Set(step.resultVisible || []);
  const topDigits = paddedDigits(tutorial.a, width);
  const multiplierDigits = paddedDigits(tutorial.b, width);
  const resultDigits = paddedDigits(tutorial.result, width);

  return (
    <div
      className="tmag-worked-columns tmag-worked-multiplication-columns"
      style={{ '--tmag-columns': width }}
      aria-label={`${formatWholeNumber(tutorial.a)} multiplicado por ${formatWholeNumber(tutorial.b)}`}
    >
      <div className="tmag-worked-row tmag-worked-carries">
        {Array.from({ length: width }, (_, index) => (
          <span key={index}>{carries.get(index) || ''}</span>
        ))}
      </div>
      <div className="tmag-worked-row tmag-worked-top">
        {topDigits.map((digit, index) => (
          <span className={step.activeTopColumn === index || step.activeColumn === index ? 'is-active' : ''} key={index}>
            {columnDigit(digit, width, index)}
          </span>
        ))}
      </div>
      <div className="tmag-worked-row tmag-worked-addend tmag-worked-multiplier">
        {multiplierDigits.map((digit, index) => (
          <span className={step.activeMultiplierColumn === index || step.activeColumn === index ? 'is-active' : ''} key={index}>
            {columnDigit(digit, width, index)}
          </span>
        ))}
      </div>
      <div className="tmag-worked-line" aria-hidden="true" />
      <div className="tmag-worked-row tmag-worked-result">
        {resultDigits.map((digit, index) => (
          <span className={visibleResult.has(index) ? 'is-visible' : ''} key={index}>
            {visibleResult.has(index) ? columnDigit(digit, width, index) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function PlanetFivePartialProductsVisual({ tutorial, step }) {
  const width = tutorial.gridWidth;
  const carries = new Map((step.carries || []).map(carry => [carry.gridColumn, carry.value]));
  const allColumns = Array.from({ length: width }, (_, index) => index);
  const firstVisible = new Set(step.showFirstPartial ? allColumns : (step.firstVisible || []));
  const secondVisible = new Set(step.showSecondPartial ? allColumns : (step.secondVisible || []));
  const resultVisible = new Set(step.showResult ? allColumns : (step.resultVisible || []));
  const topDigits = paddedDigits(tutorial.a, width);
  const multiplierDigits = paddedDigits(tutorial.b, width);
  const firstDigits = paddedDigits(tutorial.firstPartial, width);
  const secondDigits = paddedDigits(tutorial.secondPartialValue, width);
  const resultDigits = paddedDigits(tutorial.result, width);
  const activeSumColumn = Number.isInteger(step.activeSumColumn) ? step.activeSumColumn : null;
  const firstWrittenZeroColumns = new Set(step.firstWrittenZeroColumns || []);
  const secondWrittenZeroColumns = new Set(step.secondWrittenZeroColumns || []);

  // En la cuadrícula de productos parciales las columnas ya expresan valor
  // posicional. El separador se reserva exclusivamente para el resultado final.
  const finalResultColumnDigit = (digits, digit, index) => {
    if (!digit) return '';
    const firstDigitIndex = digits.findIndex(value => value !== '');
    const beginsGroup = firstDigitIndex >= 0
      && index > firstDigitIndex
      && (digits.length - index) % 3 === 0;
    return beginsGroup ? ',' + digit : digit;
  };

  const renderDigits = (
    digits,
    className,
    visible,
    activeColumn = null,
    showActiveZero = false,
    prefix = null,
    writtenZeroColumns = new Set(),
    formatThousands = false,
  ) => (
    <div className={'tmag-worked-row ' + className} style={prefix ? { position: 'relative' } : undefined}>
      {prefix ? (
        <i
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            fontStyle: 'normal',
          }}
        >
          {prefix}
        </i>
      ) : null}
      {digits.map((digit, index) => {
        const isVisible = visible instanceof Set ? visible.has(index) : visible;
        const isActive = activeColumn === index;
        const shouldShowWrittenZero = writtenZeroColumns.has(index);
        const displayedValue = isVisible
          ? (digit || (shouldShowWrittenZero || (showActiveZero && isActive) ? '0' : ''))
          : '';
        const displayDigit = isVisible && digit
          ? (formatThousands ? finalResultColumnDigit(digits, digit, index) : digit)
          : displayedValue;
        return (
          <span className={(isActive ? 'is-active ' : '') + (isVisible ? 'is-visible' : '')} key={index}>
            {displayDigit}
          </span>
        );
      })}
    </div>
  );

  return (
    <div
      className="tmag-worked-columns tmag-worked-multiplication-columns tmag-worked-partial-products tmag-worked-planet-five-products"
      style={{ '--tmag-columns': width }}
      aria-label={`${formatWholeNumber(tutorial.a)} multiplicado por ${formatWholeNumber(tutorial.b)} con productos parciales`}
    >
      <div className="tmag-worked-row tmag-worked-carries">
        {Array.from({ length: width }, (_, index) => (
          <span key={index}>{carries.get(index) || ''}</span>
        ))}
      </div>
      {renderDigits(topDigits, 'tmag-worked-top', true, step.activeTopColumn)}
      {renderDigits(multiplierDigits, 'tmag-worked-addend tmag-worked-multiplier', true, step.activeMultiplierColumn)}
      <div className="tmag-worked-line" aria-hidden="true" />
      {renderDigits(
        firstDigits,
        'tmag-worked-partial-one',
        firstVisible,
        activeSumColumn,
        tutorial.firstPartial !== 0,
        null,
        firstWrittenZeroColumns,
      )}
      {renderDigits(
        secondDigits,
        'tmag-worked-p5-second',
        secondVisible,
        activeSumColumn,
        true,
        step.showSumLine ? '+' : null,
        secondWrittenZeroColumns,
      )}
      <div
        className="tmag-worked-line tmag-worked-p5-sum-line"
        style={{ opacity: step.showSumLine ? 1 : 0 }}
        aria-hidden="true"
      />
      {renderDigits(
        resultDigits,
        'tmag-worked-result',
        resultVisible,
        activeSumColumn,
        false,
        null,
        new Set(),
        Boolean(step.showResult),
      )}
    </div>
  );
}

function PartialProductsVisual({ tutorial, step }) {
  const width = tutorial.gridWidth;
  const topDigits = paddedDigits(tutorial.a, width);
  const multiplierDigits = paddedDigits(tutorial.b, width);
  const firstDigits = paddedDigits(tutorial.firstPartial, width);
  const secondDigits = paddedDigits(tutorial.secondPartialBase, width, 1);
  const resultDigits = paddedDigits(tutorial.result, width);

  const renderRow = (digits, className, visible) => (
    <div className={'tmag-worked-row ' + className}>
      {digits.map((digit, index) => (
        <span className={visible ? 'is-visible' : ''} key={index}>
          {visible ? columnDigit(digit, width, index) : ''}
        </span>
      ))}
    </div>
  );

  return (
    <div
      className="tmag-worked-columns tmag-worked-multiplication-columns tmag-worked-partial-products"
      style={{ '--tmag-columns': width }}
      aria-label={`${formatWholeNumber(tutorial.a)} multiplicado por ${formatWholeNumber(tutorial.b)} con productos parciales`}
    >
      {renderRow(topDigits, 'tmag-worked-top', true)}
      {renderRow(multiplierDigits, 'tmag-worked-addend tmag-worked-multiplier', true)}
      <div className="tmag-worked-line" aria-hidden="true" />
      {renderRow(firstDigits, 'tmag-worked-partial-one', Boolean(step.showFirstPartial))}
      {renderRow(secondDigits, 'tmag-worked-partial-two', Boolean(step.showSecondPartial))}
      <div className="tmag-worked-line tmag-worked-partial-line" aria-hidden="true" />
      {renderRow(resultDigits, 'tmag-worked-result', Boolean(step.showResult))}
    </div>
  );
}

function WorkedVisual({ tutorial, step }) {
  if (tutorial.kind === 'planet-five-partial-products') {
    return <PlanetFivePartialProductsVisual tutorial={tutorial} step={step} />;
  }
  if (tutorial.kind === 'planet-two-columns-multiplication') {
    return <PlanetTwoMultiplicationColumnsVisual tutorial={tutorial} step={step} />;
  }
  if (tutorial.kind === 'columns-multiplication') {
    return <MultiplicationColumnsVisual tutorial={tutorial} step={step} />;
  }
  if (tutorial.kind === 'partial-products-multiplication') {
    return <PartialProductsVisual tutorial={tutorial} step={step} />;
  }
  return <EqualGroupsVisual tutorial={tutorial} step={step} />;
}

export function MultiplicationWorkedExampleTutorial({ tutorial, onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const lastStep = Math.max(0, (tutorial?.tutorial?.steps?.length || 1) - 1);
    const requestedStep = Number.isInteger(tutorial?.initialStepIndex)
      ? tutorial.initialStepIndex
      : 0;
    setStepIndex(Math.min(Math.max(0, requestedStep), lastStep));
  }, [tutorial?.sessionId]);

  const step = tutorial?.tutorial?.steps?.[stepIndex];
  const finalStep = Boolean(tutorial && stepIndex === tutorial.tutorial.steps.length - 1);
  const progress = useMemo(
    () => tutorial ? `${stepIndex + 1} de ${tutorial.tutorial.steps.length}` : '',
    [tutorial, stepIndex],
  );

  if (!tutorial || !step) return null;

  return (
    <section
      className={'tmag-worked-overlay tmag-multiplication-theme' + (tutorial.tutorial.kind === 'planet-five-partial-products' ? ' tmag-multiplication-planet-five-theme' : '')}
      role="dialog"
      aria-modal="true"
      aria-labelledby="multiplication-worked-example-title"
    >
      <div className="tmag-worked-dialog">
        <header className="tmag-worked-header">
          <span className="tmag-worked-orb" aria-hidden="true">✦</span>
          <div>
            <p>{progress}</p>
            <h2 id="multiplication-worked-example-title">VEAMOS CÓMO SE HACE</h2>
          </div>
        </header>

        <div className="tmag-worked-step">
          <span className="tmag-worked-step-badge">{step.label}</span>
          <h3>{step.title}</h3>
          <p>{step.message}</p>
          <WorkedVisual tutorial={tutorial.tutorial} step={step} />
        </div>

        <button
          className={'tmag-worked-next' + (finalStep ? ' is-final' : '')}
          type="button"
          onClick={() => finalStep ? onComplete() : setStepIndex(current => current + 1)}
        >
          {finalStep ? 'SEGUIR PRACTICANDO  →' : 'SIGUIENTE  →'}
        </button>
      </div>
    </section>
  );
}

export default MultiplicationWorkedExampleTutorial;
